import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import {
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { asapScheduler, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { IconComponent } from "~app/common/components/icon/icon.component";
import { PillboxComponent } from "~app/common/components/pillbox/pillbox.component";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { environment } from "~environments/environment";
import { AtgardHeaderIconsComponent } from "../../atgarder/atgard-header-icons/atgard-header-icons.component";
import { Atgard } from "../../models/atgard-models";
import { AllSkogsvardTradslagService } from "../../services/all-skogsvard-tradslag.service";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { SkogsvardForyngringService } from "../../services/skogsvard-foryngring.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationIconPopoverComponent } from "../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { SkogsvardBadgeComponent } from "../skogsvard-badge/skogsvard-badge.component";

@Component({
    selector: "rask-skogsvard-header",
    templateUrl: "./skogsvard-header.component.html",
    styleUrls: ["./skogsvard-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SkogsvardBadgeComponent,
        VirkeskopValidationIconPopoverComponent,
        AtgardHeaderIconsComponent,
        NgClass,
        AsyncPipe,
        DecimalPipe,
        VirkeskopValidationIconPopoverComponent,
        PillboxComponent,
        NgbPopover,
        IconComponent,
        AsyncPipe,
        DecimalPipe
    ]
})
export class SkogsvardHeaderComponent implements OnInit, OnDestroy {
    @Input() index: number;

    protected hasTradslag: boolean;
    protected tradslagLista: string;

    private readonly allSkogsvardService = inject(AllSkogsvardTradslagService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly skogsvardForyngringService = inject(SkogsvardForyngringService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    protected get kontrakt$() {
        return this.kontraktMediatorService.kontrakt$;
    }

    protected get skogsvardsAtgard(): Atgard {
        return  this.atgardMediatorService.atgard;
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.allSkogsvardService.tradslagChanged$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(() => {
                const skogsvardtradslag = this.allSkogsvardService.getTradslagForAtgard(this.skogsvardsAtgard.guid);
                this.tradslagLista = skogsvardtradslag.map(x => x).join(", ");
                asapScheduler.schedule(() => {
                    this.hasTradslag = skogsvardtradslag.length > 0 && skogsvardtradslag[0] !== "";
                });
            });
    }

    protected getMal(): number {
        const malAntal = this.isSadd() ? this.skogsvardsAtgard?.malFrogivaHa : this.skogsvardsAtgard?.malPlantorHa;

        if(!_.isNil(malAntal) && !_.isNaN(malAntal)) {
            return malAntal;
        }
        return 0;
    }

    protected getMalPlantorEnhet(): string {
        if (this.isSadd()) {
            return "kg/ha";
        } else  {
            return "PL/ha";
        }
    }

    protected hoverRow(atgardGuid: string, mouseover: boolean) {
        this.vkKartaMediatorService.highlightAtgarder([atgardGuid], mouseover);
    }

    protected visaNettoareal(): boolean {
        return !this.skogsvardsAtgard.isPlantleverans;
    }

    protected visaPlHa(): boolean {
        return !this.skogsvardsAtgard.isRojning && !this.skogsvardsAtgard.isPlantleverans;
    }

    protected visaSvpStatus() {
        return !this.skogsvardsAtgard.isPlantleverans && this.skogsvardsAtgard.skogsvardStatus;
    }

    protected hasSvpStatus() {
        return ObjectUtils.hasValue(this.skogsvardsAtgard.skogsvardStatus);
    }
    protected svpLink = (): string => `${environment.svpPortalBaseUrl}/studio/arbetsorder/${this.skogsvardsAtgard?.beteckning}`;

    private isSadd() {
        return this.skogsvardForyngringService.isSadd(this.skogsvardsAtgard);
    }
}
